import { Component, Element, Host, Listen, h } from '@stencil/core';
import { APIEvents, CustomAPI, setAPI } from '@sydle/ui-api';
import type { Route } from '@sydle/ui-utils';
import {
  Debounce,
  Provider,
  deleteURLParam,
  getAction,
  getConfig,
  getRouter,
  reloadPageContent,
  urlFor,
} from '@sydle/ui-utils';
import type { Commands, Context, IndexedParams, RouterLocation } from '@vaadin/router';
import { Router } from '@vaadin/router';
import { validateAccess } from '../../actions/validate-access';
import { validateQueryParams } from '../../actions/validate-query-params';
import type { UIAppConfig } from '../../types/ui-app-config';

type LastPageParams = {
  name: string;
  params: IndexedParams;
  search: string;
  hash: string;
};

@Component({
  tag: 'sy-app-root',
  scoped: true,
})
export class AppRoot {
  @Element() el: HTMLSyAppRootElement;

  private provider: Provider;
  private uiAppConfig: UIAppConfig | null;

  @Listen(APIEvents.LOGIN, { target: 'document' })
  async onLogin(): Promise<void> {
    const currentRouteIdentifier = getRouter()?.location?.route?.name;
    if (currentRouteIdentifier !== this.uiAppConfig?.authRoute) {
      reloadPageContent();
      return;
    }

    const redirectPage: LastPageParams | null = JSON.parse(sessionStorage.getItem('lastPage'));
    const pathname = urlFor(redirectPage?.name ?? this.uiAppConfig?.redirectRoute, redirectPage?.params);
    Router.go({ pathname: pathname, search: redirectPage?.search, hash: redirectPage?.hash });
    sessionStorage.removeItem('lastPage');
  }

  @Listen(APIEvents.LOGOUT, { target: 'document' })
  async onLogout(): Promise<void> {
    this.reloadWindow();
  }

  @Listen('vaadin-router-location-changed', { target: 'window' })
  async onVaadinRouterLocationChanged(event: CustomEvent<{ router: Router; location: RouterLocation }>): Promise<void> {
    const { router, location } = event.detail;

    if (!window.SYDLE) window.SYDLE = {};
    if (!window.SYDLE.router) window.SYDLE.router = {};
    window.SYDLE.router.instance = router;

    if (!window.sydleApp) window.sydleApp = {};
    if (!window.sydleApp.router) window.sydleApp.router = {};
    window.sydleApp.router.instance = router;

    this.provider.update('ui-app.location', location);

    deleteURLParam('accessToken');
    deleteURLParam('signOut');
    deleteURLParam('language');
  }

  componentWillLoad() {
    h('div', { color: '', key: '1' });

    const SYDLE_APP_DATA_CLASS_ID = '645be3fb34d9110770e73fd8';
    const syAppAPI = new CustomAPI(SYDLE_APP_DATA_CLASS_ID);
    setAPI('syAppAPI', syAppAPI);

    this.provider = new Provider(this.el);
    this.provider.provides('ui-app.location', null);

    this.uiAppConfig = getConfig<UIAppConfig>('ui-app');

    this.createRouter();
  }

  disconnectedCallback() {
    this.provider.disconnect();
  }

  private createRouter() {
    // Map an SYDLE APP Route to a Vaadin Route. Since we won't use
    // Vaadin to render components, the 'component' field will be set to a
    // 'div', but the element will never be rendered given that the defined
    // router outlet will not be added to the DOM.
    const router = new Router();
    const mapUIAppRoutesToVaadin = (uiAppRoutes: Route[]) => {
      return uiAppRoutes?.map((uiAppRoute) => ({
        routeId: uiAppRoute._id,
        path: uiAppRoute.path,
        name: uiAppRoute.name,
        action: async (context: Context, commands: Commands) => {
          const userDefinedActions = getAction(uiAppRoute.path) || getAction(uiAppRoute.name);

          const actionFunctions = [validateQueryParams, validateAccess, userDefinedActions];

          for (const action of actionFunctions) {
            const actionReturn = await action?.(context, commands, router);
            if (actionReturn) {
              return actionReturn;
            }
          }
        },
        component: uiAppRoute.children ? undefined : 'div',
        children: mapUIAppRoutesToVaadin(uiAppRoute.children as Route[]),
      }));
    };

    const routes = mapUIAppRoutesToVaadin(this.uiAppConfig?.routes);

    // Set fake outlet. Prevent Vaadin to render any element to the DOM.
    router.setOutlet(document.createElement('div'));
    router.setRoutes(routes);
  }

  @Debounce(50)
  private reloadWindow() {
    window.location.reload();
  }

  render() {
    return (
      <Host>
        <sy-app-custom-page></sy-app-custom-page>
      </Host>
    );
  }
}
